
import { useEffect, useMemo, useRef, useState } from "react";
import { TbReport } from "react-icons/tb";
import { useDispatch } from "react-redux";
import { setSubLoading } from '../../Redux/Services/AuthSlice';
import { NotificationSound } from '../CommonComponents/CommonNotification';
import { ApiCall, DropDownData, getDateAndTime, handleDateChange, sanitizeInput } from '../CommonComponents/CustomHooks';
import { initializeFilterData } from '../CommonComponents/EnvConfig';
import { handleDownloadExcel } from '../CommonComponents/ExportData';
import {  DateBox, FormButton, InputBox, SelectInputBox } from '../CommonComponents/InputBox';
import { OperationalTable } from '../CommonComponents/OperationalTable';

const BulkDocumentPrint = () => {
    const inputrefs = useRef([])
    const dispatch = useDispatch([])
    const [loading, setLoading] = useState(false);
    const [tableData, setTableData] = useState({ Data: [] });
    const [selectedDockets, setSelectedDockets] = useState([]);
    const [currentPageNo, setCurrentPageNo] = useState(1);
    const [filterData, setFilterData] = useState(initializeFilterData)
    const GetLabelType = DropDownData('GetLabelType')
    const [sortConfig, setSortConfig] = useState({ SortColumn: null, SortOrder: null });
    const [formData, setFormData] = useState({
        fromDate: getDateAndTime(7).date,
        toDate: getDateAndTime().date,
        client: '',
        docketNo: '',
        labelType: "CNPrint"
    });

    const getBulkDocumentPrintData = async (CurrentPage) => {
        let PageIndex = 1
        if (CurrentPage !== undefined && typeof (CurrentPage) !== "object") {
            PageIndex = CurrentPage;
            setCurrentPageNo(CurrentPage);
        }
        else {
            setCurrentPageNo(1);
        }
        let GetData = {
            ...filterData,
            ...formData,
            ...sortConfig,
            "PageIndex": PageIndex.toString(),
            "Type": "S",
        }
        setLoading(true)
        let data = await ApiCall('/api/Shipment/ShowBulkDocumentPrint', GetData)
        setTableData(data)
        setLoading(false)
        if (data.Status === 'EXPIRED') {
            await NotificationSound(data.Status, data.Message, data.focus, inputrefs);
        }
    }

    const handleClear = () => {
        setFormData({
            ...formData,
            fromDate: getDateAndTime(7).date,
            toDate: getDateAndTime().date,
            client: '',
            docketNo: '',
            labelType: "CNPrint"
        })
        setCurrentPageNo(1);
        inputrefs.current['txtFromDate']?.focus();
    };

    const handlePrint = async (docketNo) => {
        await dispatch(setSubLoading(true));
        const payload = {
            ...formData,
            docketNo: selectedDockets.join(',') || docketNo
        }
        let data = await ApiCall(`/api/Operation/AwbCNPrint`, payload);
        await dispatch(setSubLoading(false));
        if (data.status === "SUCCESS" && data.documentPdfBase64) {
            const byteCharacters = atob(data.documentPdfBase64);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = "CNDocument.pdf";
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } else {
            await NotificationSound(data.status, data.message, data?.focus, inputrefs, handleClear);
        }
    };

    const handleChange1 = (event, value) => {
        setCurrentPageNo(value);
        getBulkDocumentPrintData(value, formData);
    };

    const handleSorting = (SortColumn) => {
        let SortOrder = 'ASC';
        if (sortConfig && sortConfig.SortColumn === SortColumn && sortConfig.SortOrder === 'ASC') {
            SortOrder = 'DESC';
        }
        setSortConfig({ SortColumn, SortOrder });
        setCurrentPageNo(1);
    };

    const selectedCount =
        tableData?.Data?.filter(item => item.checkStatus === true)?.length || 0;


    const handleCheck = (checked, docketNo, index) => {
        const updatedData = tableData?.Data?.map((item, i) =>
            i === index
                ? { ...item, checkStatus: checked }
                : item
        );
        setTableData(prev => ({
            ...prev,
            Data: updatedData
        }));
        if (checked) {
            setSelectedDockets(prev => [...prev, docketNo]);
        } else {
            setSelectedDockets(prev =>
                prev.filter(item => item !== docketNo)
            );
        }
    };

    const handleCheckAll = (value) => {
        const updatedData = tableData?.Data?.map(item => ({
            ...item,
            checkStatus: value
        }));
        setTableData(prev => ({
            ...prev,
            Data: updatedData
        }));
        if (value) {
            setSelectedDockets(
                tableData?.Data?.map(item => item.DocketNo) || []
            );
        } else {
            setSelectedDockets([]);
        }
    };

    const handleSeeAndPrint = async (docketNo) => {
        await dispatch(setSubLoading(true));
        const payload = {
            ...formData,
            CallType: "Multiple",
            docketNo: selectedDockets.join(',') || docketNo
        }
        let data = await ApiCall(`/api/Operation/AwbCNPrint`, payload);
        await dispatch(setSubLoading(false));
        await NotificationSound(data.status, data.message, data?.focus, inputrefs, handleClear);
    }

    useEffect(() => {
        inputrefs.current['txtFromDate'].focus()
        document.title = 'Bulk Document Print'
        getBulkDocumentPrintData();
    }, [sortConfig]);

    return (
        <>
            <div className='bg-white rounded-lg shadow-sm p-3 mb-6'>
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                        <TbReport className="w-5 h-5 sm:w-6 sm:h-6 text-[var(--theme-color)]" />
                        <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100">Bulk Document Print</h2>
                    </div>
                </div>
                <div className='card-body pb-1'>
                    <div className='row'>
                        <div className='col-md-12'>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                                    <DateBox
                                        inputrefs={inputrefs}
                                        label="From Date"
                                        placeholder="DD-MMM-YYYY"
                                        id='txtFromDate'
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                inputrefs.current['txtToDate'].focus()
                                            };
                                        }}
                                        maxDate={new Date()}
                                        selected={formData.fromDate === '' ? '' : new Date(formData.fromDate)}
                                        onChange={(e) => handleDateChange(e, setFormData, formData, 'fromDate')}
                                    />
                                    <DateBox
                                        inputrefs={inputrefs}
                                        label="To Date"
                                        placeholder="DD-MMM-YYYY"
                                        id='txtToDate'
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                inputrefs.current['txtDocketNo'].focus()
                                            };
                                        }}
                                        maxDate={new Date()}
                                        selected={formData.toDate === '' ? '' : new Date(formData.toDate)}
                                        onChange={(e) => handleDateChange(e, setFormData, formData, 'toDate')}
                                    />
                                </div>
                                {/*<AutoCompleteBoxNew*/}
                                {/*    id="txtClient"*/}
                                {/*    placeholder="Enter Client"*/}
                                {/*    label='Client'*/}
                                {/*    inputrefs={inputrefs}*/}
                                {/*    onKeyDown={(e) => {*/}
                                {/*        if (e.key === 'Enter') {*/}
                                {/*            inputrefs.current['txtDocketNo'].focus()*/}
                                {/*        }*/}
                                {/*    }}*/}
                                {/*    autocompleteType='GetClient'*/}
                                {/*    value={formData.client || ''}*/}
                                {/*    onChange={async (event, newInputValue) => {*/}
                                {/*        newInputValue = newInputValue || ''*/}
                                {/*        if (event.type !== 'keydown') {*/}
                                {/*            setFormData({ ...formData, client: newInputValue })*/}
                                {/*        }*/}
                                {/*    }}*/}
                                {/*    maxLength='50'*/}
                                {/*/>*/}

                                <InputBox
                                    id="txtDocketNo"
                                    label="Docket No"
                                    placeholder="Enter Docket No"
                                    value={formData.docketNo}
                                    onChange={(e) =>
                                        setFormData({ ...formData, docketNo: sanitizeInput(e.target.value) })
                                    }
                                    inputrefs={inputrefs}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            inputrefs.current['txtLabelType']?.focus();
                                        }
                                    }}
                                />

                                <SelectInputBox
                                    id="txtLabelType"
                                    value={formData.labelType}
                                    label='Label Type'
                                    onChange={(e) => setFormData({ ...formData, labelType: e.target.value })}
                                    inputrefs={inputrefs}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {


                                            inputrefs.current['txtbtnSub'].focus();
                                        }
                                    }}
                                >
                                    {GetLabelType?.data?.dropDownData?.map((item, index) =>
                                        <option key={item.textPart + index} value={item.valuePart}>{item.textPart}</option>
                                    )}
                                </SelectInputBox>
                            </div>
                        </div>
                    </div>


                </div>
                <div className='card-body'>
                    <FormButton
                        id={'txtbtnSub'}
                        inputrefs={inputrefs}
                        handleSearch={getBulkDocumentPrintData}
                        save={false}
                        search={true}
                        handleExport={async () => {
                            await dispatch(setSubLoading(true))
                            await handleDownloadExcel(`/api/Shipment/ShowBulkDocumentPrint`, { ...formData, ...filterData }, 'Download Label Request  Report')
                            await dispatch(setSubLoading(false))
                        }}
                        handleClear={handleClear}
                        SeeAndPrint={selectedCount > 1}
                        handleSeeAndPrint={handleSeeAndPrint}
                    />
                </div>
            </div>
            <OperationalTable
                loading={loading}
                PageName={'BulkDocumentPrint'}
                handleFilter={() => {
                    // refetch handled automatically by useQuery when filterData changes
                }}
                filterData={filterData}
                tableData={tableData}
                handleCheck={handleCheck}
                handleCheckAll={handleCheckAll}
                setFilterData={setFilterData}
                currentPageNo={currentPageNo}
                handlePrint={handlePrint}
                handleChange1={handleChange1}
                handleSorting={handleSorting}
                sortConfig={sortConfig}
                Filter={false}
                onlyFilterPage={true}
            />
        </>
    )
}
export default BulkDocumentPrint;